import axios from 'axios';
import { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let _cache = null;
let _promise = null;

export async function fetchSale() {
  if (_cache) return _cache;
  if (!_promise) {
    _promise = axios
      .get(`${BACKEND_URL}/api/sale`)
      .then((r) => {
        _cache = { ...r.data, loaded: true };
        return _cache;
      })
      .catch(() => ({ sales: [], active: false, percent: 0, loaded: true }));
  }
  return _promise;
}

// Normalized list of currently-active sales (supports legacy single-sale shape).
function activeSales(saleData) {
  if (!saleData) return [];
  let list = Array.isArray(saleData.sales) ? saleData.sales : null;
  if (!list && saleData.percent) list = [saleData];
  if (!list) return [];
  const now = new Date();
  return list.filter((s) => s && s.percent && s.endsAt && now < new Date(s.endsAt));
}

// Best active sale covering this product/cart item (undefined item = site-wide only match rules still apply).
function saleFor(saleData, item) {
  const matches = activeSales(saleData).filter((s) => {
    const cats = s.categories;
    if (!Array.isArray(cats) || cats.length === 0) return true;
    return !!item && cats.includes(item.category);
  });
  if (matches.length === 0) return null;
  return matches.reduce((a, b) =>
    b.percent > a.percent || (b.percent === a.percent && new Date(b.endsAt) > new Date(a.endsAt)) ? b : a
  );
}

export function isSaleActive(saleData) {
  return activeSales(saleData).length > 0;
}

export function saleAppliesTo(saleData, item) {
  return !!saleFor(saleData, item);
}

// End of the sale covering this item (ISO string) or null.
export function saleEndsAt(saleData, item) {
  const s = saleFor(saleData, item);
  return s ? s.endsAt : null;
}

export function salePercent(saleData, item) {
  const s = saleFor(saleData, item);
  return s ? s.percent : 0;
}

// Discounted price (2 decimals) when a live sale covers the item, else original.
// Mirrors backend sale.apply_sale exactly.
export function salePrice(price, saleData, item) {
  const s = saleFor(saleData, item);
  if (s) return Math.round(price * (1 - s.percent / 100) * 100) / 100;
  return price;
}

// Sale eligible for banner/countdown display: only inside its last-days window
// (server provides countdownFrom). Pass an item to scope to that product; omit
// it to consider all sales. Returns the soonest-ending qualifying sale or null.
export function countdownSale(saleData, item) {
  const now = new Date();
  const pool = item === undefined ? activeSales(saleData) : activeSales(saleData).filter((s) => {
    const cats = s.categories;
    return !Array.isArray(cats) || cats.length === 0 || (!!item && cats.includes(item.category));
  });
  const windowed = pool.filter((s) => !s.countdownFrom || now >= new Date(s.countdownFrom));
  if (windowed.length === 0) return null;
  return windowed.reduce((a, b) => (new Date(a.endsAt) < new Date(b.endsAt) ? a : b));
}

// React hook: returns the current sale data, refreshed once on mount.
export function useSale() {
  const [sale, setSale] = useState(_cache || { sales: [], active: false, percent: 0, loaded: false });
  useEffect(() => {
    let alive = true;
    fetchSale().then((s) => alive && setSale(s));
    return () => {
      alive = false;
    };
  }, []);
  return sale;
}

export const fmtPrice = (n) =>
  `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
